import { useApplicationForm } from "@/context/ApplicationFormContext";
import { useProfile } from "@/context/ProfileContext";
import {
  RecipientType,
  RECIPIENT_TYPE_LABELS,
  RECIPIENT_TYPE_DESCRIPTIONS,
  validateIBAN,
  validateEmail,
  validatePhone,
  validateEDRPOU,
  validateLegalAddress,
} from "@/lib/application";
import { cn } from "@/lib/utils";
import { useState, useCallback } from "react";
import { Link } from "react-router-dom";

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
    </svg>
  );
}

function KeyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

const RECIPIENT_ICONS: Record<RecipientType, React.ComponentType<{ className?: string }>> = {
  osbb: BuildingIcon,
  manager: KeyIcon,
  cooperative: UsersIcon,
};

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  hint?: string;
  isIdGov?: boolean;
}

function Field({ label, required, error, children, hint, isIdGov }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-[13px] font-medium flex items-center gap-2">
        {label}
        {required && <span className="text-destructive">*</span>}
        {isIdGov && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">
            <LockIcon className="w-3 h-3" />
            id.gov.ua
          </span>
        )}
      </label>
      {children}
      {hint && !error && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      {error && <p className="text-[11px] text-destructive">{error}</p>}
    </div>
  );
}

export function StepRecipient() {
  const { formData, updateRecipient, loadedFromProfile, resetRecipientToProfile } = useApplicationForm();
  const { hasStoredProfile, idGovFields } = useProfile();
  const { recipient } = formData;
  
  // Validation errors state
  const [ibanError, setIbanError] = useState<string | null>(null);
  const [ibanBankName, setIbanBankName] = useState<string | null>(null);
  const [isValidatingBank, setIsValidatingBank] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [edrpouError, setEdrpouError] = useState<string | null>(null);
  const [legalAddressError, setLegalAddressError] = useState<string | null>(null);

  const handleTypeSelect = (type: RecipientType) => {
    updateRecipient({ recipient_type: type });
  };

  const handleIbanChange = (value: string) => {
    const formatted = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 29);
    updateRecipient({ iban: formatted });
    setIbanBankName(null); // Clear bank name while typing

    if (formatted.length === 29) {
      // Quick sync validation (checksum only)
      const result = validateIBAN(formatted);
      setIbanError(result.valid ? null : result.error || null);
    } else if (formatted.length > 0 && formatted.length < 29) {
      setIbanError(null); // Don't show error while typing
    } else {
      setIbanError(null);
    }
  };

  // Async bank name lookup on blur - always try to show bank name if found
  const handleIbanBlur = useCallback(async (value: string) => {
    if (!value || value.length === 0) return;
    
    const cleanValue = value.replace(/\s/g, "").toUpperCase();
    
    // Validate checksum (blocking validation)
    const basicResult = validateIBAN(cleanValue);
    setIbanError(basicResult.valid ? null : basicResult.error || null);
    
    // Always try to lookup bank name if we have enough characters for bank code
    // Bank code is at positions 4-10 (after "UA" + 2 control digits)
    if (cleanValue.length >= 10 && cleanValue.startsWith("UA")) {
      const bankCode = cleanValue.substring(4, 10);
      setIsValidatingBank(true);
      try {
        const { validateBankCode } = await import("@/lib/nbu");
        const bankResult = await validateBankCode(bankCode);
        setIbanBankName(bankResult.bankName || null);
      } catch (error) {
        console.warn("NBU bank lookup failed:", error);
        setIbanBankName(null);
      } finally {
        setIsValidatingBank(false);
      }
    } else {
      setIbanBankName(null);
    }
  }, []);

  const handlePhoneChange = (value: string) => {
    let digits = value.replace(/\D/g, "");
    if (!digits.startsWith("380")) {
      if (digits.startsWith("0")) {
        digits = "38" + digits;
      } else if (!digits.startsWith("38")) {
        digits = "380" + digits;
      }
    }
    const formatted = "+" + digits.slice(0, 12);
    updateRecipient({ phone: formatted });
    
    // Validate when user has entered enough digits
    if (formatted.length === 13) { // +380XXXXXXXXX = 13 chars
      const result = validatePhone(formatted);
      setPhoneError(result.valid ? null : result.error || null);
    } else {
      setPhoneError(null);
    }
  };

  const handlePhoneBlur = (value: string) => {
    // Validate on blur if not empty
    if (value && value.length > 4) { // More than just "+380"
      const result = validatePhone(value);
      setPhoneError(result.valid ? null : result.error || null);
    }
  };

  const handleEmailChange = (value: string) => {
    updateRecipient({ email: value });
    
    // Validate if email has @ symbol (user is trying to enter email)
    if (value.includes("@")) {
      const result = validateEmail(value);
      setEmailError(result.valid ? null : result.error || null);
    } else {
      setEmailError(null);
    }
  };

  const handleEmailBlur = (value: string) => {
    // Validate on blur even without @
    if (value && value.length > 0) {
      const result = validateEmail(value);
      setEmailError(result.valid ? null : result.error || null);
    }
  };

  const handleEdrpouChange = (value: string) => {
    const formatted = value.replace(/\D/g, "").slice(0, 8);
    updateRecipient({ edrpou_code: formatted });
    
    // Validate when 8 digits entered
    if (formatted.length === 8) {
      const result = validateEDRPOU(formatted);
      setEdrpouError(result.valid ? null : result.error || null);
    } else if (formatted.length > 0 && formatted.length < 8) {
      setEdrpouError(null);
    } else {
      setEdrpouError(null);
    }
  };

  const handleEdrpouBlur = (value: string) => {
    // Validate on blur if not empty
    if (value && value.length > 0) {
      const result = validateEDRPOU(value);
      setEdrpouError(result.valid ? null : result.error || null);
    }
  };

  const handleLegalAddressChange = (value: string) => {
    updateRecipient({ legal_address: value });
    
    // Clear error when typing if now valid
    if (value.length >= 10) {
      setLegalAddressError(null);
    }
  };

  const handleLegalAddressBlur = (value: string) => {
    // Validate on blur if not empty
    if (value && value.length > 0) {
      const result = validateLegalAddress(value);
      setLegalAddressError(result.valid ? null : result.error || null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile loaded indicator */}
      {loadedFromProfile && (
        <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border-2 border-green-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckIcon className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="text-[13px] font-medium text-green-800">
                Дані завантажено з профілю
              </p>
              <p className="text-[11px] text-green-600">
                Поля з id.gov.ua не можна змінювати, інші — можна
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={resetRecipientToProfile}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium text-green-700 hover:bg-green-100 transition-colors"
          >
            <RefreshIcon className="w-4 h-4" />
            Скинути
          </button>
        </div>
      )}

      {/* No profile hint */}
      {!hasStoredProfile && (
        <div className="p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
          <p className="text-[12px] text-blue-700">
            <strong>Підказка:</strong> Ви можете зберегти дані організації в{" "}
            <Link to="/settings" className="text-blue-800 underline hover:no-underline">
              налаштуваннях профілю
            </Link>
            , щоб не вводити їх щоразу при створенні нової заявки.
          </p>
        </div>
      )}

      {/* Recipient type selection */}
      <div>
        <h3 className="text-[15px] font-semibold mb-3">Форма управління</h3>
        <div className="grid gap-3">
          {(["osbb", "manager", "cooperative"] as RecipientType[]).map((type) => {
            const Icon = RECIPIENT_ICONS[type];
            const isSelected = recipient.recipient_type === type;

            return (
              <button
                key={type}
                type="button"
                onClick={() => handleTypeSelect(type)}
                className={cn(
                  "flex items-center gap-4 p-4 rounded-2xl border-[3px] text-left transition-all",
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-border/50 hover:border-foreground/20"
                )}
              >
                <div
                  className={cn(
                    "w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors",
                    isSelected ? "bg-primary/10" : "bg-muted"
                  )}
                >
                  <Icon className={cn("w-6 h-6", isSelected ? "text-primary" : "text-foreground/50")} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn("text-[14px] font-semibold", isSelected && "text-primary")}>
                    {RECIPIENT_TYPE_LABELS[type]}
                  </p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">
                    {RECIPIENT_TYPE_DESCRIPTIONS[type]}
                  </p>
                </div>
                {isSelected && (
                  <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <CheckIcon className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Show form fields only when type is selected */}
      {recipient.recipient_type && (
        <div className="space-y-6 pt-2">
          <div className="h-px bg-border/50" />

          {/* Organization details */}
          <div>
            <h3 className="text-[15px] font-semibold mb-4">Реквізити організації</h3>
            <div className="grid gap-4">
              <Field
                label="Повна назва організації"
                required
                isIdGov={idGovFields.organization_name}
              >
                <input
                  type="text"
                  value={recipient.organization_name}
                  onChange={(e) => {
                    if (!idGovFields.organization_name) {
                      updateRecipient({ organization_name: e.target.value });
                    }
                  }}
                  placeholder="Наприклад: ОСББ «Затишок»"
                  disabled={idGovFields.organization_name}
                  className={cn(
                    "w-full h-11 px-4 rounded-xl border-[3px] text-[13px] placeholder:text-muted-foreground focus:outline-none transition-colors",
                    idGovFields.organization_name
                      ? "border-blue-200 bg-blue-50 text-blue-900 cursor-not-allowed"
                      : "border-border/50 bg-white focus:border-primary/50"
                  )}
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label="Код ЄДРПОУ"
                  required
                  hint={!edrpouError ? "8 цифр" : undefined}
                  error={edrpouError || undefined}
                  isIdGov={idGovFields.edrpou_code}
                >
                  <input
                    type="text"
                    value={recipient.edrpou_code}
                    onChange={(e) => {
                      if (!idGovFields.edrpou_code) {
                        handleEdrpouChange(e.target.value);
                      }
                    }}
                    onBlur={(e) => {
                      if (!idGovFields.edrpou_code) {
                        handleEdrpouBlur(e.target.value);
                      }
                    }}
                    placeholder="12345678"
                    disabled={idGovFields.edrpou_code}
                    className={cn(
                      "w-full h-11 px-4 rounded-xl border-[3px] text-[13px] font-mono placeholder:text-muted-foreground focus:outline-none transition-colors",
                      idGovFields.edrpou_code
                        ? "border-blue-200 bg-blue-50 text-blue-900 cursor-not-allowed"
                        : edrpouError
                          ? "border-destructive/50 focus:border-destructive"
                          : "border-border/50 bg-white focus:border-primary/50"
                    )}
                  />
                </Field>

                <Field
                  label="ПІБ керівника"
                  required
                  isIdGov={idGovFields.head_full_name}
                >
                  <input
                    type="text"
                    value={recipient.head_full_name}
                    onChange={(e) => {
                      if (!idGovFields.head_full_name) {
                        updateRecipient({ head_full_name: e.target.value });
                      }
                    }}
                    placeholder="Іванов Іван Іванович"
                    disabled={idGovFields.head_full_name}
                    className={cn(
                      "w-full h-11 px-4 rounded-xl border-[3px] text-[13px] placeholder:text-muted-foreground focus:outline-none transition-colors",
                      idGovFields.head_full_name
                        ? "border-blue-200 bg-blue-50 text-blue-900 cursor-not-allowed"
                        : "border-border/50 bg-white focus:border-primary/50"
                    )}
                  />
                </Field>
              </div>

              <Field
                label="Юридична адреса"
                required
                hint={legalAddressError ? undefined : "Мінімум 10 символів"}
                error={legalAddressError || undefined}
              >
                <textarea
                  value={recipient.legal_address}
                  onChange={(e) => handleLegalAddressChange(e.target.value)}
                  onBlur={(e) => handleLegalAddressBlur(e.target.value)}
                  placeholder="Повна юридична адреса організації"
                  rows={2}
                  className={cn(
                    "w-full px-4 py-3 rounded-xl border-[3px] bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none transition-colors resize-none",
                    legalAddressError
                      ? "border-destructive/50 focus:border-destructive"
                      : "border-border/50 focus:border-primary/50"
                  )}
                />
              </Field>

              <Field label="Фактична адреса">
                <textarea
                  value={recipient.actual_address}
                  onChange={(e) => updateRecipient({ actual_address: e.target.value })}
                  placeholder="Фактична адреса (якщо відрізняється від юридичної)"
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl border-[3px] border-border/50 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors resize-none"
                />
              </Field>
            </div>
          </div>

          {/* Contact info */}
          <div>
            <h3 className="text-[15px] font-semibold mb-4">Контактна інформація</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Номер телефону"
                required
                hint={!phoneError ? "Формат: +380XXXXXXXXX" : undefined}
                error={phoneError || undefined}
              >
                <input
                  type="tel"
                  value={recipient.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onBlur={(e) => handlePhoneBlur(e.target.value)}
                  placeholder="+380"
                  className={cn(
                    "w-full h-11 px-4 rounded-xl border-[3px] bg-white text-[13px] font-mono placeholder:text-muted-foreground focus:outline-none transition-colors",
                    phoneError
                      ? "border-destructive/50 focus:border-destructive"
                      : "border-border/50 focus:border-primary/50"
                  )}
                />
              </Field>

              <Field
                label="Електронна адреса"
                required
                hint={!emailError ? "Формат: user@example.com" : undefined}
                error={emailError || undefined}
              >
                <input
                  type="email"
                  value={recipient.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={(e) => handleEmailBlur(e.target.value)}
                  placeholder="email@example.com"
                  className={cn(
                    "w-full h-11 px-4 rounded-xl border-[3px] bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none transition-colors",
                    emailError
                      ? "border-destructive/50 focus:border-destructive"
                      : "border-border/50 focus:border-primary/50"
                  )}
                />
              </Field>
            </div>
          </div>

          {/* Bank account */}
          <div>
            <h3 className="text-[15px] font-semibold mb-4">Банківський рахунок</h3>
            <Field
              label="IBAN рахунок"
              required
              error={ibanError ? (ibanBankName ? `${ibanError} (${ibanBankName})` : ibanError) : undefined}
              hint={
                ibanError 
                  ? undefined 
                  : isValidatingBank 
                    ? "Перевірка банку..." 
                    : ibanBankName 
                      ? `✓ ${ibanBankName}` 
                      : "Рахунок із спеціальним режимом використання"
              }
            >
              <input
                type="text"
                value={recipient.iban}
                onChange={(e) => handleIbanChange(e.target.value)}
                onBlur={(e) => handleIbanBlur(e.target.value)}
                placeholder="UA000000000000000000000000000"
                className={cn(
                  "w-full h-11 px-4 rounded-xl border-[3px] bg-white text-[13px] font-mono placeholder:text-muted-foreground focus:outline-none transition-colors",
                  ibanError 
                    ? "border-destructive/50 focus:border-destructive" 
                    : ibanBankName 
                      ? "border-green-300 focus:border-green-400" 
                      : "border-border/50 focus:border-primary/50"
                )}
              />
            </Field>
          </div>

          {/* Validation summary */}
          {(() => {
            const missingFields: string[] = [];
            if (!recipient.recipient_type) missingFields.push("Тип отримувача");
            if (recipient.organization_name.length < 3) missingFields.push("Назва організації");
            if (!/^\d{8}$/.test(recipient.edrpou_code)) missingFields.push("Код ЄДРПОУ");
            if (recipient.head_full_name.length < 5) missingFields.push("ПІБ керівника");
            if (recipient.legal_address.length < 10) missingFields.push("Юридична адреса");
            if (!/^\+380\d{9}$/.test(recipient.phone.replace(/\s/g, ""))) missingFields.push("Телефон");
            if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(recipient.email)) missingFields.push("Email");
            if (!/^UA\d{27}$/.test(recipient.iban)) missingFields.push("IBAN");
            
            if (missingFields.length > 0) {
              return (
                <div className="mt-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-[12px] text-amber-800 font-medium">
                    Заповніть обов'язкові поля: {missingFields.join(", ")}
                  </p>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}
    </div>
  );
}
